import Hls from 'hls.js';
import * as React from 'react';

export const useHLS = (
  videoRef: React.RefObject<HTMLVideoElement | null>,
  src: string
) => {
  const hlsRef = React.useRef<Hls | null>(null);

  React.useEffect(() => {
    if (!videoRef.current || !src) return;

    const video = videoRef.current;

    // iOS compatibility: Set webkit-playsinline for older iOS versions
    video.setAttribute('webkit-playsinline', 'true');
    video.setAttribute('playsinline', 'true');

    const isHLS =
      src.includes('.m3u8') || src.includes('application/x-mpegURL');

    // Cleanup previous HLS instance
    if (hlsRef.current) {
      hlsRef.current.destroy();
      hlsRef.current = null;
    }

    if (isHLS && Hls.isSupported()) {
      const hls = new Hls({
        xhrSetup: (xhr, _) => {
          xhr.withCredentials = true;
        },
        enableWorker: true,
        lowLatencyMode: false,
        maxBufferLength: 30,
        maxMaxBufferLength: 60,
        maxBufferSize: 60 * 1000 * 1000,
        backBufferLength: 30,
      });
      hlsRef.current = hls;
      hls.loadSource(src);
      hls.attachMedia(video);

      // Error handling for HLS
      hls.on(Hls.Events.ERROR, (_, data) => {
        if (data.fatal) {
          switch (data.type) {
            case Hls.ErrorTypes.NETWORK_ERROR: {
              // Try to recover network error
              hls.startLoad();
              break;
            }
            case Hls.ErrorTypes.MEDIA_ERROR: {
              hls.recoverMediaError();
              break;
            }
            default: {
              // Cannot recover, destroy and fallback
              hls.destroy();
              video.src = src;
              video.load();
              break;
            }
          }
        }
      });
    } else {
      // Fallback: HLS not supported, try direct source
      video.src = src;
      video.load();
    }

    // eslint-disable-next-line consistent-return
    return () => {
      if (hlsRef.current) {
        hlsRef.current.destroy();
        hlsRef.current = null;
      }
    };
  }, [src, videoRef]);

  return hlsRef;
};
